import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ShieldIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { EntitySheet } from '@/components/entity-sheet';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { Separator } from '@/components/ui/separator';
import { selectUser } from '../../../pages/login/selectors';
import AutocompleteInput from '../../autocomplete-input';
import TwoFactorAuthDialog from '../../two-factor-auth-dialog';
import {
    useChangePasswordMutation,
    useEditUserMutation,
    useUploadFileMutation,
} from '../../../services/api';
import { useNotificationsProvider } from '../../../context/NotificationsContext';
import { ReactComponent as DefaultUserIcon } from '../../../assets/ic_default_user.svg';
import { UserIcon, State } from '../../UserIcon';

EditUserDialog.propTypes = {
    title: PropTypes.string,
    open: PropTypes.bool,
    value: PropTypes.object,
    onClose: PropTypes.func,
    onEdited: PropTypes.func,
    onDelete: PropTypes.func,
    initialEditing: PropTypes.bool,
};

export default function EditUserDialog({
    title,
    open,
    value,
    onClose,
    onEdited,
    onDelete,
    initialEditing = false,
}) {
    const { t } = useTranslation();
    const loggedUser = useSelector(selectUser());
    const { showNotification } = useNotificationsProvider();

    const fileInputRef = useRef(null);

    const [editUser, { isLoading: isEditing }] = useEditUserMutation();
    const [changePassword, { isLoading: isChangingPassword }] = useChangePasswordMutation();
    const [uploadFile] = useUploadFileMutation();

    const [editing, setEditing] = useState(initialEditing);

    // Photo state stays outside the form: the file blob is uploaded on
    // change and only the resulting `photoUrl` gets sent with the
    // mutation, so RHF wouldn't add anything here.
    const [photo, setPhoto] = useState(null);
    const [photoUrl, setPhotoUrl] = useState(null);
    const [photoState, setPhotoState] = useState(null);

    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [open2FADialog, setOpen2FADialog] = useState(false);

    const isCurrentEmail = !!(
        loggedUser && value?.email && loggedUser.email === value.email
    );

    const schema = z
        .object({
            firstName: z.string().trim().min(1, t('user.errors.firstName-required', 'First name is required')),
            lastName: z.string().trim().min(1, t('user.errors.lastName-required', 'Last name is required')),
            role: z
                .any()
                .refine((v) => v && v.id !== undefined && v.id !== null, {
                    message: t('user.errors.role-required', 'Role is required'),
                }),
            oldPassword: z.string().optional(),
            password: z.string().optional(),
            confirmationPassword: z.string().optional(),
        })
        .superRefine((v, ctx) => {
            // Password fields only matter when editing self. If any of
            // the three is filled, all three must be filled and the
            // confirmation must match the new password.
            if (!isCurrentEmail) return;
            const any =
                (v.oldPassword?.length ?? 0) > 0 ||
                (v.password?.length ?? 0) > 0 ||
                (v.confirmationPassword?.length ?? 0) > 0;
            if (!any) return;
            if ((v.oldPassword?.length ?? 0) === 0) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['oldPassword'],
                    message: t(
                        'user.errors.oldPassword-required',
                        'Current password is required to change it',
                    ),
                });
            }
            if ((v.password?.length ?? 0) === 0) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['password'],
                    message: t('user.errors.password-required', 'New password is required'),
                });
            }
            if (v.confirmationPassword !== v.password) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['confirmationPassword'],
                    message: t('user.errors.passwords-do-not-match', "Passwords don't match"),
                });
            }
        });

    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            firstName: value?.firstName ?? '',
            lastName: value?.lastName ?? '',
            role: { id: value?.role, title: value?.roleName },
            oldPassword: '',
            password: '',
            confirmationPassword: '',
        },
    });

    const handleAvatarClick = () => {
        fileInputRef.current?.click();
    };

    const resetState = () => {
        form.reset({
            firstName: value?.firstName ?? '',
            lastName: value?.lastName ?? '',
            role: { id: value?.role, title: value?.roleName },
            oldPassword: '',
            password: '',
            confirmationPassword: '',
        });
        setPhoto(value?.photoUrl ? value.photoUrl : null);
        setPhotoUrl(value?.photoUrl);
        setPhotoState(null);
    };

    const onSubmit = async (values) => {
        if (
            isCurrentEmail &&
            (values.oldPassword?.length ?? 0) > 0 &&
            (values.password?.length ?? 0) > 0
        ) {
            try {
                await changePassword({
                    id: value.id,
                    oldPassword: values.oldPassword,
                    newPassword: values.password,
                }).unwrap();
            } catch {
                showNotification(
                    'error',
                    t('user.edit-user-dialog.error', { name: value.fullName }),
                );
                return;
            }
        }

        const data = {
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            role: values.role.id,
        };
        if (photoUrl) data.photoUrl = photoUrl;

        const prev = {
            firstName: value.firstName,
            lastName: value.lastName,
            role: value.role,
            photoUrl: value.photoUrl,
        };

        // Skip the edit mutation entirely if nothing changed.
        if (dataEqual(data, prev)) {
            resetState();
            onEdited();
            return;
        }

        try {
            await editUser({ id: value.id, ...data }).unwrap();
            resetState();
            showNotification(
                'success',
                t('user.edit-user-dialog.success', {
                    name: `${values.firstName} ${values.lastName}`,
                }),
            );
            onEdited();
        } catch {
            showNotification(
                'error',
                t('user.edit-user-dialog.error', { name: value.fullName }),
            );
        }
    };

    const handleImageUpload = async (event) => {
        const image = event.target.files[0];
        if (!image) return;
        setPhotoState(State.Loading);
        setPhoto(URL.createObjectURL(image));
        try {
            const result = await uploadFile({
                file: image,
                meta: { contentType: image.type },
            }).unwrap();
            setPhotoUrl(result.url);
            setPhotoState(State.Success);
        } catch {
            setPhotoState(State.Error);
            showNotification('error', t('common.api-error'));
        }
    };

    const dataEqual = (a, b) => {
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) return false;
        return aKeys.every((key) => a[key] === b[key]);
    };

    useEffect(() => {
        if (!open || !value || !value.email) return;
        form.reset({
            firstName: value.firstName ?? '',
            lastName: value.lastName ?? '',
            role: { id: value.role, title: value.roleName },
            oldPassword: '',
            password: '',
            confirmationPassword: '',
        });
        setPhoto(value.photoUrl ? value.photoUrl : null);
        setPhotoUrl(value.photoUrl);
        setPhotoState(null);
        setEditing(initialEditing);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, value?.email, loggedUser?.email, initialEditing]);

    const loading = isEditing || isChangingPassword;
    const currentValues = form.watch();

    return (
        <EntitySheet
            open={open}
            onOpenChange={(next) => {
                if (!next) {
                    if (loading) return;
                    resetState();
                    onClose();
                }
            }}
            title={title}
            editing={editing}
            onToggleEdit={setEditing}
            canEdit
            onDelete={onDelete}
            footer={
                editing ? (
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => {
                                if (loading) return;
                                setEditing(false);
                            }}
                            disabled={loading}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={form.handleSubmit(onSubmit)}
                            disabled={loading}
                        >
                            {t('common.save')}
                        </Button>
                    </div>
                ) : null
            }
        >
            {editing ? renderEditForm() : renderReadView()}
        </EntitySheet>
    );

    function renderReadView() {
        return (
            <div className="space-y-5">
                <div className="flex justify-center">
                    {photo ? (
                        <UserIcon src={photo} width={72} height={72} />
                    ) : (
                        <DefaultUserIcon />
                    )}
                </div>
                <dl className="space-y-3 text-sm">
                    <div>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                            {t('user.edit-user-dialog.firstName-title')}
                        </dt>
                        <dd className="mt-1">{currentValues.firstName || '—'}</dd>
                    </div>
                    <div>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                            {t('user.edit-user-dialog.lastName-title')}
                        </dt>
                        <dd className="mt-1">{currentValues.lastName || '—'}</dd>
                    </div>
                    <div>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                            {t('user.edit-user-dialog.email-title')}
                        </dt>
                        <dd className="mt-1">{value?.email ?? '—'}</dd>
                    </div>
                    <div>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                            {t('user.edit-user-dialog.role-title')}
                        </dt>
                        <dd className="mt-1">{currentValues.role?.title ?? '—'}</dd>
                    </div>
                </dl>
            </div>
        );
    }

    function renderEditForm() {
        return (
            <Form {...form}>
                <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-5"
                    noValidate
                >
                    {/* Avatar uploader */}
                    <div className="flex flex-col items-center">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleImageUpload}
                        />
                        <button
                            type="button"
                            onClick={handleAvatarClick}
                            className="rounded-full p-1 transition hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                            {photo ? (
                                <UserIcon
                                    src={photo}
                                    width={58}
                                    height={58}
                                    state={photoState}
                                />
                            ) : (
                                <DefaultUserIcon />
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={handleAvatarClick}
                            className="mt-1 cursor-pointer text-xs text-muted-foreground hover:underline"
                        >
                            {t('user.add-user-dialog.upload-photo')}
                        </button>
                    </div>

                    <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="after:ml-0.5 after:text-destructive after:content-['*']">
                                    {t('user.edit-user-dialog.firstName-title')}
                                </FormLabel>
                                <FormControl>
                                    <Input {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="after:ml-0.5 after:text-destructive after:content-['*']">
                                    {t('user.edit-user-dialog.lastName-title')}
                                </FormLabel>
                                <FormControl>
                                    <Input {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <div className="space-y-1.5">
                        <Label htmlFor="edit-user-email" className="text-sm font-medium">
                            {t('user.edit-user-dialog.email-title')}
                        </Label>
                        <Input
                            id="edit-user-email"
                            value={value?.email ?? ''}
                            disabled
                        />
                    </div>

                    <FormField
                        control={form.control}
                        name="role"
                        render={({ field, fieldState }) => (
                            <FormItem>
                                <AutocompleteInput
                                    error={!!fieldState.error}
                                    onChange={(val) => field.onChange(val)}
                                    defaultValue={field.value}
                                    value={field.value}
                                    label={t('user.edit-user-dialog.role-title')}
                                    resource="users/roles-autocomplete"
                                    required
                                    canDelete
                                    refreshOnInputChange
                                    clearOnBlur={false}
                                    freeSolo
                                />
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {isCurrentEmail ? (
                        <>
                            <Separator />
                            <h3 className="text-sm font-medium text-muted-foreground">
                                {t('user.edit-user-dialog.security-section', 'Security')}
                            </h3>
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full"
                                onClick={() => setOpen2FADialog(true)}
                            >
                                <ShieldIcon className="size-4" />
                                {t(
                                    'user.edit-user-dialog.manage-2fa',
                                    'Manage Two-Factor Authentication',
                                )}
                            </Button>

                            <Separator />
                            <h3 className="text-sm font-medium text-muted-foreground">
                                {t(
                                    'user.edit-user-dialog.change-password-section',
                                    'Change Password',
                                )}
                            </h3>

                            <FormField
                                control={form.control}
                                name="oldPassword"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            {t('user.edit-user-dialog.old-password-title')}
                                        </FormLabel>
                                        <FormControl>
                                            <PasswordInput
                                                show={showOldPassword}
                                                onToggleShow={() =>
                                                    setShowOldPassword(!showOldPassword)
                                                }
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="password"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            {t('user.edit-user-dialog.password-title')}
                                        </FormLabel>
                                        <FormControl>
                                            <PasswordInput
                                                show={showPassword}
                                                onToggleShow={() =>
                                                    setShowPassword(!showPassword)
                                                }
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="confirmationPassword"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            {t(
                                                'user.edit-user-dialog.confirm-password-title',
                                            )}
                                        </FormLabel>
                                        <FormControl>
                                            <PasswordInput
                                                show={showConfirmPassword}
                                                onToggleShow={() =>
                                                    setShowConfirmPassword(!showConfirmPassword)
                                                }
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </>
                    ) : null}

                    <TwoFactorAuthDialog
                        open={open2FADialog}
                        onClose={() => setOpen2FADialog(false)}
                    />
                </form>
            </Form>
        );
    }
}
